import { batchActions } from 'util/redux'

import {
  collectionQuery,
  createMailbox,
  updateMailbox,
  destroyMailbox,
  fetchMailbox,
  convertMailboxToDemo,
} from 'api/mailboxes'

import * as types from 'constants/action_types'

import { doMarkFetchingStatus, doClearFetchedStatuses } from 'actions/app'
import { doShowSnackbar } from 'actions/snackbar'
import { doOpenMainPage } from 'actions/pages'
import {
  doFetchTicketAggregationCountsForUnloadedNavigationSections,
  doFetchTicketSearchCountsForPinnedSearches,
} from 'actions/search'

import graphql from 'api/graphql'

import {
  oauthTokenSelector,
  fetchingStatusesSelector,
} from 'selectors/app/base'
import { selectCurrentMailboxId } from 'selectors/mailboxes'
import { selectMailboxesById } from 'selectors/mailboxes_base'

import debug from 'util/debug'
import { isEmpty } from 'util/objects'

export function doFetchInaccessibleMailboxes() {
  return (dispatch, getState) => {
    const state = getState()
    const fetchingStatuses = fetchingStatusesSelector(state)
    const fetchingStatusKey = 'fetchInaccessibleMailboxes'
    // ensure we never have more than one concurrent request
    if (fetchingStatuses[fetchingStatusKey]) return
    dispatch(doMarkFetchingStatus(fetchingStatusKey, true))

    const query = `
      query InaccessibleMailboxQuery {
        mailboxes(inaccessible: true) {
          id
          name
          email
          from_name
        }
      }
    `
    const token = oauthTokenSelector(state)

    graphql(token, query)
      .then(res => {
        const data = res.json.data
        dispatch(
          batchActions(
            doUpdateInaccessibleMailboxes(data.mailboxes),
            doMarkFetchingStatus(fetchingStatusKey, false)
          )
        )
      })
      .catch(error => {
        debug(error)
      })
  }
}

export function doFetchMailbox(id) {
  return (dispatch, getState) => {
    const state = getState()
    const fetchingStatuses = fetchingStatusesSelector(state)
    const fetchingStatusKey = 'fetchMailbox'
    if (fetchingStatuses[fetchingStatusKey]) return
    dispatch(doMarkFetchingStatus(fetchingStatusKey, true))
    const token = oauthTokenSelector(state)
    // eslint-disable-next-line consistent-return
    return fetchMailbox(token, id).then(mailbox => {
      dispatch(
        batchActions(
          doUpdateMailboxLocally(mailbox),
          doMarkFetchingStatus(fetchingStatusKey, false)
        )
      )
    })
  }
}

export function doFetchMailboxes() {
  return (dispatch, getState) => {
    const state = getState()

    dispatch({ type: types.FETCH_MAILBOXES_REQUEST })

    return graphql(oauthTokenSelector(state), collectionQuery())
      .then(res => {
        const data = res.json.data
        dispatch({ type: types.FETCH_MAILBOXES_SUCCESS, data: { ...data } })
      })
      .catch(err => {
        dispatch({ type: types.FETCH_MAILBOXES_FAIL, data: { err } })
        throw err
      })
  }
}

export function doCreateMailbox(mailbox, responseFields) {
  return (dispatch, getState) => {
    dispatch({ type: types.CREATE_MAILBOX_REQUEST, payload: { mailbox } })
    const state = getState()
    const token = oauthTokenSelector(state)
    return createMailbox(token, mailbox, responseFields)
      .then(newMailbox => {
        dispatch({
          type: types.CREATE_MAILBOX_SUCCESS,
          payload: { mailbox: newMailbox },
        })
        return newMailbox
      })
      .catch(error => {
        debug(error)
        dispatch({
          type: types.CREATE_MAILBOX_FAILURE,
          payload: { error: 'Unable to create mailbox, please try again' },
        })
        throw error
      })
  }
}

export function doUpdateMailbox(id, mailbox, responseFields) {
  return (dispatch, getState) => {
    dispatch({ type: types.UPDATE_MAILBOX_REQUEST, payload: { mailbox } })
    const state = getState()
    const token = oauthTokenSelector(state)
    return updateMailbox(token, id, mailbox, responseFields)
      .then(updatedMailboxFields => {
        doUpdateMailboxLocally(updatedMailboxFields)
        return updatedMailboxFields
      })
      .catch(error => {
        debug(error)
        dispatch({
          type: types.UPDATE_MAILBOX_FAILURE,
          payload: { error: 'Unable to update mailbox, please try again' },
        })
        throw error
      })
  }
}

// NOTE (jscheel): This is an old action that only updates locally. It should be
// refactored now that we have "saving" mailboxes to the api, and updating from
// realtime events.
export function doUpdateMailboxLocally(mailbox) {
  if (!mailbox) return false
  return {
    type: types.UPDATE_MAILBOX_SUCCESS,
    payload: {
      mailbox,
    },
  }
}

export function doDestroyMailbox(id) {
  return (dispatch, getState) => {
    dispatch({ type: types.DESTROY_MAILBOX_REQUEST, payload: { id } })
    const state = getState()
    const token = oauthTokenSelector(state)
    return destroyMailbox(token, id)
      .then(() => {
        dispatch({
          type: types.DESTROY_MAILBOX_SUCCESS,
          payload: { id },
        })
      })
      .catch(error => {
        debug(error)
        dispatch({
          type: types.DESTROY_MAILBOX_FAILURE,
          payload: { error: 'Unable to delete mailbox, please try again' },
        })
      })
  }
}

export function doConvertMailboxToDemo(id) {
  return (dispatch, getState) => {
    dispatch({ type: types.CONVERT_MAILBOX_TO_DEMO_REQUEST, payload: { id } })
    const state = getState()
    const token = oauthTokenSelector(state)
    return convertMailboxToDemo(token, id)
      .then(newMailbox => {
        dispatch({
          type: types.CONVERT_MAILBOX_TO_DEMO_SUCCESS,
          payload: { mailbox: newMailbox },
        })
        return newMailbox
      })
      .catch(error => {
        debug(error)
        dispatch({
          type: types.CONVERT_MAILBOX_TO_DEMO_FAILURE,
          payload: {
            error: 'Unable to convert mailbox to demo, please try again',
          },
        })
        throw error
      })
  }
}

export function doRemoveMailbox(id) {
  return (dispatch, getState) => {
    const state = getState()
    let actions = [
      {
        type: types.REMOVE_MAILBOX,
        data: id,
      },
    ]
    if (selectCurrentMailboxId(state) === id) {
      actions = [
        doShowSnackbar('Current mailbox deleted'),
        doOpenMainPage(),
      ].concat(actions)
    }
    return dispatch(batchActions(actions))
  }
}

export function doUpdateInaccessibleMailboxes(mailboxes) {
  return {
    type: types.UPDATE_INACCESSIBLE_MAILBOXES,
    data: {
      mailboxes,
    },
  }
}

export function doCurrentMailboxChanged(mailboxId) {
  return batchActions(
    doClearFetchedStatuses(/ticketAggregationCounts-/),
    doFetchTicketSearchCountsForPinnedSearches({ mailbox: mailboxId }),
    doFetchTicketAggregationCountsForUnloadedNavigationSections({
      mailbox: mailboxId,
    })
  )
}

// Conditionally fetches mailboxes, or returns those already in store.
export function doLoadMailboxes() {
  return (dispatch, getState) => {
    const state = getState()
    const mailboxes = selectMailboxesById(state)
    if (!mailboxes || isEmpty(mailboxes)) return dispatch(doFetchMailboxes())
    return Promise.resolve(mailboxes)
  }
}

export function doRememberPreviousMailbox(mailboxId) {
  return {
    type: types.REMEMBER_PREVIOUS_MAILBOX,
    data: { mailboxId },
  }
}
